import { OP } from '../../../../../app/modules/operations/core/operationPermissions'
import { PROCUREMENT_PERMS } from '../../../../../app/modules/Procurement/core/permissions'
import { QHSE_PERMS } from '../../../../../app/modules/QHSE/core/permissions'
import { TECH_PERM } from '../../../../../app/modules/Technical/core/technicalPermissions'
import { ACCOUNTS_PERMS } from '../../../../../app/modules/Accounts/core/permissions'
import { DPR_ROUTE } from '../../../../../app/modules/operations/core/routes'
import { isPermanentlyHiddenRoute } from '../../../../../app/shared/navigation/permanentlyHiddenMenus'

const normalizeRoute = (route?: string) => String(route || '').replace(/\/+$/, '').toLowerCase()

export type SidebarRouteEntry = {
  label: string
  route: string
}

/**
 * Canonical READ permission for sidebar leaf routes.
 * Return null for public routes or unmapped business routes (fail closed).
 */
export const getMenuReadPermission = (route?: string): string | null => {
  const r = normalizeRoute(route)
  if (!r) return null
  if (isPermanentlyHiddenRoute(r)) return null

  if (r === '/dashboard') return null

  // Admin — mapped to the resource each page actually lists (see ManagePage + Management/_requests)
  // /manage/companies -> CompanyAdminList -> GET /company-group-admins (Company Group Admin rows)
  if (r === '/manage/companies') return 'READ_COMPANY_GROUP_ADMIN'
  // /manage/subcompanies -> CompaniesList -> GET /users/company-admins (Company Admin rows)
  if (r === '/manage/subcompanies') return 'READ_COMPANY_ADMIN'
  if (r === '/manage/vessels') return 'READ_VESSEL'
  if (r === '/manage/usermanagement') return 'READ_USER'
  if (r === '/manage/vendormanagement') return 'READ_VENDOR'

  // Operations
  if (r === '/operations/voyages') return OP.READ_VOYAGE
  if (r === '/operations/overview') return OP.READ_POSITION_REPORT
  if (r === DPR_ROUTE) return OP.READ_DAILY_PROGRESS_REPORT
  if (r === '/operations/coastal-position-report') return OP.READ_COASTAL_POSITION_REPORT
  if (r === '/operations/cargo-operations') return OP.READ_CARGO_OPERATION
  if (r === '/operations/library') return 'READ_OPERATIONS_LIBRARY'
  if (r === '/operations/inport-consumption') return OP.READ_IN_PORT_CONSUMPTION_REPORT
  if (r === '/operations/reports/cii-summary') return OP.READ_CII_SUMMARY
  if (r === '/operations/defects-list') return 'READ_OPERATION_DEFECT'

  // Crewing
  if (r === '/crewingtest/crewinglist') return 'READ_CREW'
  if (r === '/crewing/assignment') return 'READ_CREW_ASSIGNMENT'
  if (r === '/crewingtest/resthours') return 'READ_REST_HOURS'
  if (r === '/crewing/crewovertime') return 'READ_OVERTIME_ENTRY'
  if (r === '/crewing/crewdeductions') return 'READ_DEDUCTIONS_MANAGER'
  if (r === '/crewing/crewclaims') return 'READ_CREW_CLAIM'
  if (r === '/crewing/crewallotment') return 'READ_CREW_ALLOTMENT'
  if (r === '/crewing/appraisal') return 'READ_CREW_APPRAISALS'
  if (r === '/crewing/crewtraining') return 'READ_TRAINING_TRACKER'
  if (r === '/crewing/crewcomplaints') return 'READ_CREW_COMPLAINT'

  // Technical
  if (r === '/technical/machinery') return TECH_PERM.READ_MACHINERY
  if (r === '/technical/pms/dashboard') return TECH_PERM.READ_PMS_DASHBOARD
  if (r === '/technical/pms/jobs') return TECH_PERM.READ_PMS_JOB
  if (r === '/technical/pms/running-hours') return TECH_PERM.READ_RUNNING_HOURS
  if (r === '/technical/pms/calendar-planning') return TECH_PERM.READ_PMS_CALENDAR
  if (r === '/technical/defects-list') return 'READ_TECHNICAL_DEFECT'

  // Technical > Procurement
  if (r === '/procurement/overview') return PROCUREMENT_PERMS.READ_PROCUREMENT_OVERVIEW
  if (r === '/procurement/requisition') return PROCUREMENT_PERMS.READ_REQUISITION
  if (r === '/procurement/rfq-management') return PROCUREMENT_PERMS.RFQ_VIEW
  if (r === '/procurement/quotation-management') return PROCUREMENT_PERMS.QUOTATION_VIEW
  if (r === '/procurement/purchase-orders') return PROCUREMENT_PERMS.READ_PURCHASE_ORDER
  if (r === '/procurement/approval-workflow') return 'RFQ_VIEW|READ_PURCHASE_ORDER|INVOICE_VIEW'
  if (r === '/procurement/approval-library') return PROCUREMENT_PERMS.READ_APPROVAL_LIMIT
  if (r === '/procurement/currency-exchange-rates') return PROCUREMENT_PERMS.READ_CURRENCY_EXCHANGE_RATE
  if (r === '/procurement/company-po-info') return PROCUREMENT_PERMS.READ_PURCHASE_ORDER
  if (r === '/procurement/delivery-tracking') return 'READ_DELIVERY_TRACKING'
  if (r === '/procurement/goods-receipt') return PROCUREMENT_PERMS.READ_GOODS_RECEIPT
  if (r === '/procurement/invoice-management') return PROCUREMENT_PERMS.INVOICE_VIEW
  if (r === '/procurement/audit-trail') return PROCUREMENT_PERMS.PROCUREMENT_LOG_VIEW
  if (r === '/procurement/user-management') return PROCUREMENT_PERMS.USER_PERMISSION_VIEW

  // QHSE
  if (r === '/qhse/document-library') return QHSE_PERMS.DOC_READ
  if (r === '/qhse/forms-checklists/submissions') return QHSE_PERMS.FORM_SUBMISSION_READ
  if (r === '/qhse/forms-checklists/library') return QHSE_PERMS.FORM_TEMPLATE_READ
  if (r === '/qhse/vessel-certificates') return QHSE_PERMS.CERT_READ
  if (r === '/qhse/office-certificates') return QHSE_PERMS.OFFICE_CERT_READ
  if (r === '/qhse/manuals-and-plans') return QHSE_PERMS.MANUAL_PLAN_READ
  if (r === '/qhse/defects-list') return QHSE_PERMS.DEFECT_READ
  if (r === '/qhse/audit-index') return QHSE_PERMS.AUDIT_READ
  if (r === '/qhse/audit-planning') return QHSE_PERMS.AUDIT_PLAN_READ
  if (r === '/qhse/audit-findings') return QHSE_PERMS.AUDIT_FINDING_READ
  if (r === '/qhse/inspindex') return QHSE_PERMS.INSPECTION_READ
  if (r === '/qhse/insplanning') return QHSE_PERMS.INSPECTION_PLAN_READ
  if (r === '/qhse/inspection-findings') return QHSE_PERMS.INSPECTION_FINDING_READ
  if (r === '/qhse/accident-near-miss-reports') return QHSE_PERMS.NEAR_MISS_READ
  if (r === '/qhse/risk-assessment') return QHSE_PERMS.RISK_READ
  if (r === '/qhse/safety-committee-meeting') return QHSE_PERMS.SAFETY_COMMITTEE_READ
  if (r === '/qhse/work-permits-and-checklists') return QHSE_PERMS.WORK_PERMIT_READ
  if (r === '/qhse/month-end-reports') return 'READ_QHSE_MONTH_END_REPORT'
  if (r === '/qhse/month-end-reports/main-engine-consumption') return 'READ_QHSE_MONTH_END_ME_CONSUMPTION'
  if (r === '/qhse/month-end-reports/machinery-status') return 'READ_QHSE_MONTH_END_MACHINERY_STATUS'
  if (r === '/qhse/month-end-reports/months-end-report') return 'READ_QHSE_MONTH_END_REPORT'

  // Accounts
  if (r === '/accounts/dashboard') return ACCOUNTS_PERMS.DASHBOARD_READ
  if (r === '/accounts/sales/customers') return ACCOUNTS_PERMS.CUSTOMER_READ
  if (r === '/accounts/sales/quotes') return ACCOUNTS_PERMS.QUOTE_READ
  if (r === '/accounts/sales/sales-orders') return ACCOUNTS_PERMS.SALES_ORDER_READ
  if (r === '/accounts/sales/invoices') return ACCOUNTS_PERMS.INVOICE_READ
  if (r === '/accounts/sales/recurring-invoices') return ACCOUNTS_PERMS.RECURRING_INVOICE_READ
  if (r === '/accounts/sales/delivery-challans') return ACCOUNTS_PERMS.DELIVERY_CHALLAN_READ
  if (r === '/accounts/sales/payments-received') return ACCOUNTS_PERMS.PAYMENT_RECEIVED_READ
  if (r === '/accounts/sales/credit-notes') return ACCOUNTS_PERMS.CREDIT_NOTE_READ
  if (r === '/accounts/purchase/vendors') return ACCOUNTS_PERMS.VENDOR_READ
  if (r === '/accounts/purchase/expenses') return ACCOUNTS_PERMS.EXPENSE_READ
  if (r === '/accounts/purchase/recurring-expenses') return ACCOUNTS_PERMS.RECURRING_EXPENSE_READ
  if (r === '/accounts/purchase/purchase-orders') return ACCOUNTS_PERMS.PURCHASE_ORDER_READ
  if (r === '/accounts/purchase/bills') return ACCOUNTS_PERMS.BILL_READ
  if (r === '/accounts/purchase/recurring-bills') return ACCOUNTS_PERMS.RECURRING_BILL_READ
  if (r === '/accounts/purchase/payments-made') return ACCOUNTS_PERMS.PAYMENT_MADE_READ
  if (r === '/accounts/purchase/vendor-credits') return ACCOUNTS_PERMS.VENDOR_CREDIT_READ
  if (r === '/accounts/budget-expense') return ACCOUNTS_PERMS.BUDGET_READ
  if (r === '/accounts/invoices') return ACCOUNTS_PERMS.VESSEL_EXPENSE_INVOICE_READ

  return null
}

const walkMenuRoutes = (nodes: any[] | undefined, path: string[], out: SidebarRouteEntry[]) => {
  if (!Array.isArray(nodes)) return
  nodes.forEach((node) => {
    const route = String(node?.route || '').trim()
    const label = String(node?.name || node?.title || route || 'unknown')
    const nextPath = [...path, label]
    if (route && route !== '#') {
      out.push({ label: nextPath.join(' > '), route })
    }
    if (Array.isArray(node?.submenu) && node.submenu.length > 0) {
      walkMenuRoutes(node.submenu, nextPath, out)
    }
  })
}

/** Collect every routed sidebar entry from a menu tree (for audit/tests). */
export const collectSidebarRouteEntries = (menuGroups: any[] | undefined): SidebarRouteEntry[] => {
  const out: SidebarRouteEntry[] = []
    ; (menuGroups || []).forEach((group) => walkMenuRoutes(group?.menu, [], out))
  return out
}

/** Business routes that intentionally remain unmapped and fail closed until canonical READ exists. */
export const UNMAPPED_BUSINESS_ROUTES = new Set([
  '/manage',
  '/operations',
  '/operations/dashboard',
  '/operations/offshore-report',
  '/operations/form-index',
  '/operations/reports',
  '/operations/reports/kpis',
  '/operations/reports/voyage-abstract',
  '/operations/reports/cargo-plan-log',
  '/crewing',
  '/crewing/resthours',
  '/technical',
  '/technical/pms',
  '/technical/procurement',
  '/technical/monthly-log',
  '/procurement/credit-note',
  '/qhse',
  '/qhse/forms-checklists',
  '/qhse/audits-and-inspection',
  '/qhse/vessel-audits',
  '/qhse/vessel-inspection',

  // Accounts — parent / group routes fail closed; leaf routes have canonical permissions
  '/accounts',
  '/accounts/sales',
  '/accounts/purchase',
  '/accounts/vessel-expenses',
])
